// Shared horizontal status stepper — originally built for the Job Detail
// status bar (Settings > Job Status) and reused for the Estimate workflow
// bar (Fix B, 2026-08-21) so both stay visually consistent without
// duplicating the same JSX in two pages.
//
// `sideKeys` is the only addition needed for Estimates: statuses in that
// list are NOT part of the sequential main flow (no connecting line, no
// past/current/future coloring) — they render as separate pills using
// their own `color`, because they are an EXIT from the flow (e.g.
// "Rejected", "Converted to Invoice"), not an intermediate step. Job Detail
// passes no `sideKeys`, so its rendering/behavior is unchanged.

export const STEPPER_COLORS = [
  { key: 'gray', label: 'Gray', swatch: 'bg-gray-400' },
  { key: 'blue', label: 'Blue', swatch: 'bg-blue-500' },
  { key: 'sky', label: 'Sky', swatch: 'bg-sky-500' },
  { key: 'green', label: 'Green', swatch: 'bg-emerald-500' },
  { key: 'red', label: 'Red', swatch: 'bg-red-500' },
  { key: 'amber', label: 'Amber', swatch: 'bg-amber-500' },
  { key: 'indigo', label: 'Indigo', swatch: 'bg-indigo-500' },
  { key: 'violet', label: 'Violet', swatch: 'bg-violet-500' },
];

const SIDE_PILL_CLASSES = {
  gray: { active: 'bg-gray-600 text-white border-gray-600', idle: 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100' },
  blue: { active: 'bg-blue-600 text-white border-blue-600', idle: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
  sky: { active: 'bg-sky-600 text-white border-sky-600', idle: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100' },
  green: { active: 'bg-emerald-600 text-white border-emerald-600', idle: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
  red: { active: 'bg-red-600 text-white border-red-600', idle: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
  amber: { active: 'bg-amber-600 text-white border-amber-600', idle: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
  indigo: { active: 'bg-indigo-600 text-white border-indigo-600', idle: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' },
  violet: { active: 'bg-violet-600 text-white border-violet-600', idle: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100' },
};

export const StatusStepper = ({
  statuses = [],
  currentKey,
  onSelect,
  canEdit = true,
  sideKeys = [],
  testidPrefix = 'status-badge',
  listTestId,
  labelFn,
}) => {
  const mainFlow = statuses.filter(s => !sideKeys.includes(s.key));
  const sideFlow = statuses.filter(s => sideKeys.includes(s.key));
  const curIdx = mainFlow.findIndex(s => s.key === currentKey);

  const getLabel = (s) => (labelFn ? labelFn(s) : s.label);

  const handleClick = (key) => {
    if (!canEdit || !onSelect || key === currentKey) return;
    onSelect(key);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center flex-1 overflow-x-auto py-0.5" data-testid={listTestId}>
        {mainFlow.map((s, idx) => {
          const isCurrent = currentKey === s.key;
          const isPast = curIdx > -1 && idx < curIdx;
          return (
            <div key={s.key} className="flex items-center flex-shrink-0">
              <button
                type="button"
                onClick={() => handleClick(s.key)}
                data-testid={`${testidPrefix}-${s.key}`}
                className={`px-3 py-2 sm:px-2.5 sm:py-1 rounded-full text-xs sm:text-[11px] font-semibold whitespace-nowrap transition-colors border ${
                  isCurrent
                    ? 'bg-[#003366] text-white border-[#003366] shadow-sm'
                    : isPast
                      ? 'bg-blue-50 text-[#003366] border-blue-100 hover:bg-blue-100'
                      : 'bg-gray-50 text-gray-400 border-gray-100 hover:bg-gray-100 hover:text-gray-600'
                } ${!canEdit ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {getLabel(s)}
              </button>
              {idx < mainFlow.length - 1 && (
                <div className={`w-3 sm:w-5 h-[2px] flex-shrink-0 ${isPast ? 'bg-[#003366]/40' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
      {sideFlow.length > 0 && (
        <div className="flex items-center gap-1.5 flex-shrink-0 border-l border-gray-200 pl-2">
          {sideFlow.map((s) => {
            const isCurrent = currentKey === s.key;
            const palette = SIDE_PILL_CLASSES[s.color] || SIDE_PILL_CLASSES.gray;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => handleClick(s.key)}
                data-testid={`${testidPrefix}-${s.key}`}
                className={`px-3 py-2 sm:px-2.5 sm:py-1 rounded-full text-xs sm:text-[11px] font-semibold whitespace-nowrap transition-colors border ${
                  isCurrent ? palette.active : palette.idle
                } ${!canEdit ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {getLabel(s)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatusStepper;
